// components/modals/VerificationModal.jsx
import React, { useRef } from "react";
import { Modal } from "antd";
import { useLanguage } from "../../context/LanguageContext";

const CODE_LENGTH = 6;

export default function VerificationModal({
  open,
  onClose,
  email,
  code,
  setCode,
  loading,
  onVerify,
  onResend,
  resendLoading,
  resendCooldown,
}) {
  const { t, isRTL   } = useLanguage();
  const inputsRef = useRef([]);

  const digits = code.padEnd(CODE_LENGTH, "").split("").slice(0, CODE_LENGTH);

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setCode(newDigits.join(""));
    if (digit && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = "";
        setCode(newDigits.join(""));
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        const newDigits = [...digits];
        newDigits[index - 1] = "";
        setCode(newDigits.join(""));
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(pasted.padEnd(CODE_LENGTH, "").slice(0, CODE_LENGTH));
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
  };

  return (
    <Modal
      title={t("verification.title")}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      maskClosable={false}
    >
      <div className="space-y-4">
        <p className="text-gray-600">
          {t("verification.sentTo")} <strong>{email}</strong>
        </p>

        <div>
          <label className="text-textPrimary mb-2 block text-lg">
            {t("verification.codeLabel")}
          </label>

          {/* OTP Square Inputs */}
          <div className={`flex ${isRTL ? "flex-row-reverse" : "flex-row"} gap-2 justify-center`}>
            {Array.from({ length: CODE_LENGTH }).map((_, index) => (
              <input
                key={index}
                ref={(el) => (inputsRef.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digits[index] || ""}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={`
                  w-11 h-11 text-center text-xl font-bold rounded-lg border-2 outline-none
                  transition-all duration-200
                  ${digits[index]
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-gray-300 bg-white text-gray-800"}
                  focus:border-primary focus:ring-2 focus:ring-primary/20
                  caret-transparent
                `}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={onVerify}
            disabled={loading || code.replace(/\s/g, "").length < CODE_LENGTH}
            className={`w-full py-3 px-6 text-white text-lg font-semibold rounded-xl ${
              loading || code.replace(/\s/g, "").length < CODE_LENGTH
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-primary hover:bg-primary_hover"
            } transition duration-200`}
          >
            {loading ? t("verification.verifying") : t("verification.verifyButton")}
          </button>

          <div className="text-center">
            <span className="text-gray-600">{t("verification.didntReceive")} </span>
            <button
              onClick={onResend}
              disabled={resendLoading || resendCooldown > 0}
              className={`text-primary font-semibold ${
                resendLoading || resendCooldown > 0
                  ? "cursor-not-allowed opacity-50"
                  : "hover:underline"
              }`}
            >
              {resendLoading
                ? t("verification.sending")
                : resendCooldown > 0
                ? t("verification.resendIn").replace("{{seconds}}", resendCooldown)
                : t("verification.resendCode")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}